import React, { useEffect, useMemo, useState } from "react";
import { Checkbox, Loader, TextField } from "@vibe/core";
import { Modal, ModalHeader, ModalContent, ModalFooter, ModalBasicLayout } from "@vibe/core/next";
import { getColumnIcon } from "../../../utils/columnIcons";
import { normalizeColumnType } from "../../../utils/normalizeColumnType";
import useTokenHook from "../../../hooks/context/useTokenHook";
import logger from "../../../lib/logger";
import { useShareColumnContext } from "./ShareColumnContext";
import useShareColumnBoards from "./useShareColumnBoards";
import {
  BoardFailure,
  estimateBackfillMinutes,
  LARGE_BACKFILL_ITEM_COUNT,
  shareColumn,
  ShareColumnResult,
} from "./shareColumn";
import useShareColumnBackfillPreview from "./useShareColumnBackfillPreview";
import {
  ColumnIdentity,
  pickSourceHolder,
  splitBoardsByColumn,
  validateColumnName,
} from "./shareColumnEligibility";

interface ShareColumnDialogProps {
  identity: ColumnIdentity;
  onClose: () => void;
}

const VIEW_ONLY_ACCESS = "view";

const describeItemCount = (count: number) =>
  count === 0
    ? "No connected items to fill yet"
    : `Fills ${count.toLocaleString()} connected item${count === 1 ? "" : "s"}`;

const ShareColumnDialog = ({ identity, onClose }: ShareColumnDialogProps) => {
  const context = useShareColumnContext();
  const { token } = useTokenHook();
  const linkedBoards = useMemo(() => context?.linkedBoards ?? [], [context?.linkedBoards]);

  const { data, error, isLoading, mutate } = useShareColumnBoards(linkedBoards, true);

  const [name, setName] = useState(identity.title);
  const [selectedBoardIds, setSelectedBoardIds] = useState<Set<string> | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [failures, setFailures] = useState<BoardFailure[]>([]);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // After a rename the column goes by `name` on the boards that hold it, so a retry of the
  // failed boards has to look it up under the title it has now.
  const [currentIdentity, setCurrentIdentity] = useState(identity);

  const { holders, missing } = useMemo(
    () => splitBoardsByColumn(linkedBoards, data?.columnsByBoardId ?? {}, currentIdentity),
    [linkedBoards, data?.columnsByBoardId, currentIdentity],
  );

  const isViewOnly = (boardId: string) => data?.accessByBoardId?.[boardId] === VIEW_ONLY_ACCESS;

  // Default to every board the user can write to, once the live columns are in.
  useEffect(() => {
    if (!data || selectedBoardIds !== null) return;
    setSelectedBoardIds(
      new Set(
        missing
          .filter((board) => data.accessByBoardId?.[board.boardId] !== VIEW_ONLY_ACCESS)
          .map((board) => board.boardId),
      ),
    );
  }, [data, missing, selectedBoardIds]);

  const selectedTargets = missing.filter((board) => selectedBoardIds?.has(board.boardId));
  const source = pickSourceHolder(holders, context?.currentBoardId ?? "");
  const isRenaming = name.trim() !== currentIdentity.title;

  const preview = useShareColumnBackfillPreview(currentIdentity, source, holders, selectedTargets);

  const validation = validateColumnName({
    name,
    identity: currentIdentity,
    holders,
    selectedTargets,
    columnsByBoardId: data?.columnsByBoardId ?? {},
  });

  const toggleBoard = (boardId: string) => {
    setSelectedBoardIds((previous) => {
      const next = new Set(previous ?? []);
      if (next.has(boardId)) next.delete(boardId);
      else next.add(boardId);
      return next;
    });
  };

  const handleConfirm = async () => {
    if (!source || !token || selectedTargets.length === 0 || validation.error) return;

    setSubmitting(true);
    setSubmitError(null);
    setFailures([]);

    let result: ShareColumnResult;
    try {
      result = await shareColumn({
        identity: currentIdentity,
        name,
        holders,
        source,
        targets: selectedTargets,
        token,
      });
    } catch (error: any) {
      logger.error("Share column failed", { message: error?.message });
      setSubmitError(error?.message ?? "Something went wrong. Try again.");
      setSubmitting(false);
      return;
    }

    setSubmitting(false);

    if (result.renameError) {
      setSubmitError(result.renameError);
      // A rename can stop halfway through the holders; reload so the dialog shows real titles.
      await mutate();
      return;
    }

    if (result.created.length > 0) context?.onShared(result, currentIdentity.type);

    if (result.failed.length === 0) {
      onClose();
      return;
    }

    // Keep the dialog open on the boards that failed so the user can retry just those.
    setCurrentIdentity({ title: result.name, type: currentIdentity.type });
    setFailures(result.failed);
    setSelectedBoardIds(new Set(result.failed.map((failure) => failure.board.boardId)));
    await mutate();
  };

  const typeLabel = normalizeColumnType(identity.type).replace(/_/g, " ");
  const confirmText =
    selectedTargets.length > 1 ? `Add to ${selectedTargets.length} boards` : "Add column";

  return (
    <Modal id="share-column-modal" size="medium" show onClose={onClose}>
      <ModalBasicLayout>
        <ModalHeader title="Add column to other boards" className="px-8" />
        <ModalContent>
          {isLoading ? (
            <div className="flex justify-center items-center py-6 mx-auto">
              <Loader size="small" color="primary" />
            </div>
          ) : error ? (
            <div className="flex flex-col gap-3 items-center py-4 mx-auto text-center">
              <p className="text-sm">Couldn't load the connected boards' columns.</p>
              <button type="button" className="text-sm underline" onClick={() => mutate()}>
                Retry
              </button>
            </div>
          ) : !source ? (
            <p className="px-3 text-sm">
              This column is no longer on any connected board. Refresh the item view and try again.
            </p>
          ) : missing.length === 0 ? (
            <p className="px-3 text-sm">
              Every connected board already has a {typeLabel} column named "{currentIdentity.title}
              ".
            </p>
          ) : (
            <div className="flex flex-col gap-4 px-3">
              <div className="flex gap-2 items-center text-sm text-light-secondary-text-color">
                <span className="flex items-center">{getColumnIcon(identity.type)}</span>
                <span className="capitalize">{typeLabel} column</span>
                <span>·</span>
                <span>on {holders.map(({ board }) => board.boardName).join(", ")}</span>
              </div>

              <div>
                <TextField
                  id="share-column-name"
                  title="Column name"
                  value={name}
                  onChange={(value: string) => setName(value)}
                  disabled={submitting}
                  validation={
                    validation.error && Object.keys(validation.conflictsByBoardId).length === 0
                      ? { status: "error", text: validation.error }
                      : undefined
                  }
                />
                {isRenaming && !validation.error && (
                  <p className="mt-1 text-xs text-light-secondary-text-color">
                    Also renames the column on{" "}
                    {holders.map(({ board }) => board.boardName).join(", ")} so the names keep
                    matching and it stays in sync.
                  </p>
                )}
              </div>

              <div>
                <p className="mb-2 text-sm font-medium">Add it to</p>
                <div className="rounded border border-solid border-[#D0D4E4] divide-y divide-[#D0D4E4]">
                  {missing.map((board) => {
                    const viewOnly = isViewOnly(board.boardId);
                    const conflict = validation.conflictsByBoardId[board.boardId];
                    const failure = failures.find((item) => item.board.boardId === board.boardId);

                    return (
                      <div key={board.boardId} className="flex flex-col gap-1 px-3 py-2">
                        <Checkbox
                          label={board.boardName}
                          checked={!viewOnly && !!selectedBoardIds?.has(board.boardId)}
                          disabled={viewOnly || submitting}
                          onChange={() => toggleBoard(board.boardId)}
                        />
                        {!viewOnly &&
                          selectedBoardIds?.has(board.boardId) &&
                          preview.perTargetBoard !== undefined && (
                            <p className="pl-6 text-xs text-light-secondary-text-color">
                              {describeItemCount(preview.perTargetBoard[board.boardId] ?? 0)}
                            </p>
                          )}
                        {viewOnly && (
                          <p className="pl-6 text-xs text-light-secondary-text-color">
                            You have view-only access to this board.
                          </p>
                        )}
                        {!viewOnly && conflict && selectedBoardIds?.has(board.boardId) && (
                          <p className="pl-6 text-xs text-[#D83A52]">
                            {conflict}. Rename the column above to add it here.
                          </p>
                        )}
                        {failure && (
                          <p className="pl-6 text-xs text-[#D83A52]">
                            Couldn't add the column. {failure.message}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="flex flex-col gap-1 p-3 text-sm rounded bg-[#F6F7FB] text-light-secondary-text-color">
                <p>
                  Values are copied from {source.board.boardName} to every item it shares with the
                  boards you pick, this one included.
                </p>
                {(preview.itemCount ?? 0) > LARGE_BACKFILL_ITEM_COUNT && (
                  <p className="font-medium text-[#323338]">
                    This copies {preview.itemCount!.toLocaleString()} values and takes about{" "}
                    {estimateBackfillMinutes(preview.itemCount!)} minutes in the background. You can
                    keep working meanwhile.
                  </p>
                )}
              </div>

              {submitError && <p className="text-sm text-[#D83A52]">{submitError}</p>}
            </div>
          )}
        </ModalContent>
      </ModalBasicLayout>

      <ModalFooter
        primaryButton={{
          onClick: handleConfirm,
          text: failures.length > 0 ? "Retry" : confirmText,
          loading: submitting,
          disabled:
            isLoading ||
            !!error ||
            !source ||
            submitting ||
            selectedTargets.length === 0 ||
            !!validation.error,
        }}
        secondaryButton={{
          onClick: onClose,
          text: failures.length > 0 ? "Close" : "Cancel",
          disabled: submitting,
        }}
      />
    </Modal>
  );
};

export default ShareColumnDialog;
